import { NetworkConfig } from '../signing/types';

type ValidationSuccess = { valid: true; config: NetworkConfig };
type ValidationFailure = { valid: false; error: string };
type ValidationResult = ValidationSuccess | ValidationFailure;

export function validateNetworkConfigJson(input: string): ValidationResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch (e) {
    const message = e instanceof SyntaxError ? e.message : 'Invalid JSON';
    return { valid: false, error: `JSON parse error: ${message}` };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { valid: false, error: 'Expected a JSON object' };
  }

  const obj = parsed as Record<string, unknown>;

  if (typeof obj.version !== 'number') {
    return { valid: false, error: 'Missing or invalid field: "version" (expected number)' };
  }

  if (typeof obj.name !== 'string' || obj.name.trim() === '') {
    return { valid: false, error: 'Missing or invalid field: "name" (expected non-empty string)' };
  }

  if (typeof obj.nodes !== 'object' || obj.nodes === null || Array.isArray(obj.nodes)) {
    return { valid: false, error: 'Missing or invalid field: "nodes" (expected object)' };
  }

  const nodes = obj.nodes as Record<string, unknown>;
  if (Object.keys(nodes).length === 0) {
    return { valid: false, error: 'Field "nodes" must have at least one endpoint' };
  }

  for (const [key, value] of Object.entries(nodes)) {
    if (typeof value !== 'string') {
      return { valid: false, error: `Invalid node value for "${key}" (expected string)` };
    }
  }

  if (typeof obj.mirrorNode !== 'string' || obj.mirrorNode.trim() === '') {
    return {
      valid: false,
      error: 'Missing or invalid field: "mirrorNode" (expected non-empty string)',
    };
  }

  if (typeof obj.tls !== 'boolean') {
    return { valid: false, error: 'Missing or invalid field: "tls" (expected boolean)' };
  }

  if (obj.explorer !== undefined && obj.explorer !== null && typeof obj.explorer !== 'string') {
    return { valid: false, error: 'Invalid field: "explorer" (expected string or null)' };
  }

  if (obj.rpcRelay !== undefined && obj.rpcRelay !== null && typeof obj.rpcRelay !== 'string') {
    return { valid: false, error: 'Invalid field: "rpcRelay" (expected string or null)' };
  }

  return {
    valid: true,
    config: {
      version: obj.version as number,
      name: obj.name as string,
      nodes: obj.nodes as Record<string, string>,
      mirrorNode: obj.mirrorNode as string,
      explorer: (obj.explorer as string) ?? null,
      rpcRelay: (obj.rpcRelay as string) ?? null,
      tls: obj.tls as boolean,
    },
  };
}
